/* eslint-disable prettier/prettier */
/* eslint-disable react/prop-types */

import { debounce } from 'lodash';
import moment from 'moment-timezone';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getReportData, getTotalCount } from '../../../../../api/reports';
import { Loader, Pagination, Table } from '../../../../../common/components';
import { useSetLoading } from '../../../../../common/hooks/appearance';
import { usePagination } from '../../../../../common/hooks/data';
import {
  arAdjustmentTableColumns,
  fetchDashboardFilterData,
  reports,
} from '../../../../../utils/constant';
import * as handlers from '../../../../../utils/handlers';
import DownloadModal from '../../../downloadModal/downloadModal';
import EmailModal from '../../../emailModal/emailModal';
import ItemEmptyTile from '../../../item-empty-tile/item-empty-tile';
import Toolbar from '../../../toolbar/toolbar';
import onDownloadClick from '../../onDownloadClickHandler';
import ArAdjustmentReportRow from './arAdjustmentReportRow';

const ArAdjustmentReport = ({ fromDate, toDate, tableData, totalResults }) => {
  const filterData = useSelector((state) => state.filters.dashboardMultiSelectFilters);
  const [arAdjustmentTableData, setArAdjustmentTableData] = useState([]);
  const { page, limit, setPage, setLimit } = usePagination();
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [applyFilters, setApplyFilters] = useState(false);
  const [slStartNumber, setSlStartNumber] = useState(null);
  const [totalCount, setTotalCount] = useState(null);
  const [initialRenderDone, setInitialRenderDone] = useState(false);
  useEffect(() => {
    if (!initialRenderDone) {
      setInitialRenderDone(true);
      return;
    }
    getUpdatedTableData('globalFilterChange');
  }, [filterData]);
  const getUpdatedTableData = async (type = 'pagination', value = '') => {
    setLoading(true);
    setApplyFilters(false);

    let page2 = page;
    let limit2 = limit;
    if (type === 'search' || type === 'globalFilterChange') {
      page2 = 1;
      limit2 = 5;
    }
    try {
      const requestParams = {
        ...fetchDashboardFilterData(filterData),
        startDate: fromDate,
        endDate: toDate,
        page: page2,
        limit: limit2,
        timezone: moment.tz.guess(),
        ...((type === 'search' || searchText.length > 0) && {
          opiOrName: type === 'search' ? value : searchText,
        }),
      };
      const { data } = await getReportData('arAdjustmentReport', requestParams);
      if (type === 'search' || type === 'globalFilterChange') {
        setPage(1);
        setLimit(5);
        const response = await getTotalCount('arAdjustmentReport', {
          ...fetchDashboardFilterData(filterData),
          startDate: fromDate,
          endDate: toDate,
          opiOrName: type === 'search' ? value : searchText,
          timezone: moment.tz.guess(),
        });
        setTotalCount(response?.data?.data?.totalCount);
      }
      setArAdjustmentTableData(data.data?.items || []);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      handlers.apiErrorHandler(error);
    }
  };
  const debounceApiCall = useCallback(
    debounce((val) => {
      getUpdatedTableData('search', val);
    }, 2000),
    [],
  );
  const onSearchInputChange = (event) => {
    const {
      target: { value },
    } = event;
    setSearchText(value);
    debounceApiCall(value);
  };
  useEffect(() => {
    setArAdjustmentTableData(tableData);
    setTotalCount(totalResults);
    setPage(1);
    setLimit(5);
    setSearchText('');
  }, [tableData, setLimit, totalResults, setPage]);

  useEffect(() => {
    setSlStartNumber(page * limit - limit);
    if (applyFilters) {
      getUpdatedTableData('pagination');
    }
  }, [page, limit, applyFilters]);

  const toggleEmailModal = () => {
    if (arAdjustmentTableData?.length) {
      setIsEmailModalOpen(!isEmailModalOpen);
    }
  };

  const toggleDownloadModal = () => {
    if (arAdjustmentTableData?.length) {
      setIsDownloadModalOpen(!isDownloadModalOpen);
    }
  };
  const LoaderComponent = () => {
    if (loading) {
      return <Loader />;
    }
    return <ItemEmptyTile />;
  };
  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <div>
      <Toolbar
        title="AR Adjustment by type"
        optionOneLabel="Email Report"
        optionOneIcon="MdOutlineEmail"
        onOptionOneClick={toggleEmailModal}
        optionTwoLabel="Download Report"
        optionTwoIcon="FiDownload"
        onOptionTwoClick={toggleDownloadModal}
        searchText={searchText}
        onSerchInputChange={onSearchInputChange}
        isSearchable
        SearchInputPlaceholder="Search using OPI or Contract Number"
        isTableDataPresent={arAdjustmentTableData?.length > 0}
      />
      {arAdjustmentTableData && arAdjustmentTableData?.length > 0 ? (
        <>
          <div className="table-resp-scroll">
            <Table
              headers={arAdjustmentTableColumns}
              nestedHeaders
              bordered
              responsive
              className="dashboard-table ar-adjustment-report"
            >
              <tbody>
                {arAdjustmentTableData?.map((rowData, rowNumber) => (
                  <ArAdjustmentReportRow
                    key={slStartNumber + rowNumber + 1}
                    rowData={rowData}
                    id={slStartNumber + rowNumber + 1}
                    nestedColumnsNumber={Object.keys(arAdjustmentTableColumns)?.length - 2}
                  />
                ))}
              </tbody>
            </Table>
            {loading && <Loader />}
          </div>
          <div className="pagination-araging-report">
            <Pagination
              totalResults={totalCount}
              page={page}
              limit={limit}
              setPage={(pageNo) => {
                setApplyFilters(true);
                setPage(pageNo);
              }}
              setLimit={(rowlimit) => {
                setApplyFilters(true);
                setLimit(rowlimit);
              }}
            />
          </div>
        </>
      ) : (
        <div className="loader-empty">
          <LoaderComponent />
        </div>
      )}
      <EmailModal
        opiOrName={searchText}
        isOpen={isEmailModalOpen}
        toggle={toggleEmailModal}
        reportType={reports.AR_ADJUSTMENT_REPORT}
        apiValues={{
          startDate: fromDate,
          endDate: toDate,
          ...(searchText && { opiOrName: searchText }),
        }}
        newFilters
      />
      <DownloadModal
        opiOrName={searchText}
        isOpen={isDownloadModalOpen}
        toggle={toggleDownloadModal}
        reportType={reports.AR_ADJUSTMENT_REPORT}
        urlParams={{
          startDate: fromDate,
          endDate: toDate,
          action: 'download',
          ...(searchText && { opiOrName: searchText }),
        }}
        onDownloadClick={onDownloadClick}
        willReceiveReportFileInEmail
        newFilters
      />
    </div>
  );
};
export default ArAdjustmentReport;
